import { __rest } from "tslib";
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { SectionState } from '../../store/section/SectionState';
import { PromptHeader, ReportPromptHeaderId } from '../PromptHeader';
import { ReportContextMenu } from '../ReportContextMenu';
import { ReportSections } from './internal/ReportSections';
import { HiddenSectionDialog } from '../HiddenSectionDialog';
import { BirdActionMenu } from '../BirdActionMenu';
import { ReportContextMenuState } from '../../store/reportContextMenu/ReportContextMenuState';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { CSSVars } from '../utils/cssVars';
import { useDesignTokens } from '@sas/nova-commons/theme';
import { useContextMenuHandlers } from '../hooks';
import { exposeTestId } from '../../config';
import { Breadcrumb } from '@sas/nova-commons/components/Breadcrumb';
import { ExplanationInfoDialog } from '../ExplanationInfoDialog';
import { ManageColumnsDialog } from '../ManageColumnsDialog';
import { getUriParameterBoolean } from '@sas-dvr/ltjs-commons/ltjsUtil';
import { selectPromptHeaderFlexDirection } from '../../store/selectors';
//css
import cn from './ReportContainer.css.json';
import './ReportContainer.css';
import * as cnHack from './BreadcrumbHack.css.json';
import './BreadcrumbHack.css';
const pageTabRedesignEnabled = getUriParameterBoolean('VASDK_TABREDESIGN', false);
export const ReportContainerBase = forwardRef((_a, ref) => {
    var _b, _c, _d;
    var { hideNavigation, onReportLoad, actions, contextMenuActions, headerFlexDirection } = _a, state = __rest(_a, ["hideNavigation", "onReportLoad", "actions", "contextMenuActions", "headerFlexDirection"]);
    const [hasOnLoadFired, setHasOnLoadFired] = useState(false);
    const dt = useDesignTokens();
    const pageTabRedesign = state.pageTabRedesign || pageTabRedesignEnabled;
    const backgroundColorTokens = useCallback((dt) => ({
        bg: `var(${CSSVars.BackgroundColor}, ${pageTabRedesign ? dt.backgrounds.panes.bg : dt.backgrounds.page.bg})`,
    }), [pageTabRedesign]);
    const tabTokens = useCallback((dt, { selected }) => {
        // NOTE: force strict property checking
        const tokens = selected || pageTabRedesign
            ? {}
            : {
                bg: `var(${CSSVars.InactiveTabBg}, var(${CSSVars.BackgroundColor}, ${dt.backgrounds.page.bg}))`,
                fg: `var(${CSSVars.InactiveTabFg}, ${dt.stateStandard.normal.fg})`,
            };
        return tokens;
    }, [pageTabRedesign]);
    const sectionData = state.currentSection ? state.sections[state.currentSection] : undefined;
    const loadedRootId = sectionData ? sectionData.rootContainerId : null;
    useEffect(() => {
        if (!hasOnLoadFired && !!loadedRootId && onReportLoad) {
            onReportLoad();
            setHasOnLoadFired(true);
        }
    }, [hasOnLoadFired, loadedRootId, onReportLoad]);
    const { onContextMenu, onKeyDown } = useContextMenuHandlers((event) => {
        contextMenuActions.setReportContextMenuTarget(null, event.nativeEvent.timeStamp);
        contextMenuActions.open(event);
    });
    const currentSectionLabel = (state.currentSection && ((_b = state.sections[state.currentSection]) === null || _b === void 0 ? void 0 : _b.label)) || '';
    const linkBreadcrumb = ((_c = state.linkLabels) === null || _c === void 0 ? void 0 : _c.length) ? (React.createElement(Breadcrumb, { orientation: "HORIZONTAL", className: cnHack.hack, selectCrumb: (index) => {
            const maxIndex = state.linkLabels.length - 1;
            // For page switching we queue up pages to avoid getting in a bad state
            // We have no way of queuing up link navigation
            if (state.transitioning || index > maxIndex) {
                return;
            }
            actions.notifyLinkNavigation(maxIndex - index);
        }, values: [
            {
                isSingle: true,
                selectedIndex: 0,
                values: [state.reportLabel],
            },
            ...state.linkLabels
                .map((val) => ({
                isSingle: true,
                values: [val],
                selectedIndex: 0,
            }))
                .reverse()
                .slice(1),
            {
                isSingle: true,
                selectedIndex: 0,
                values: [currentSectionLabel],
            },
        ] })) : undefined;
    const maximized = state.maximizedVisual && !state.hiddenSection;
    // Tracks hover state of report header to notify page header when it should change its background color
    const [promptHeaderHover, setPromptHeaderHover] = useState(undefined);
    if (!state.currentSection || !sectionData || !sectionData.rootContainerId) {
        // TODO: This is really a recoverable error case. Is there a better way to handle this?
        return null;
    }
    else {
        return (React.createElement("div", { className: cn.reportContainerWrapper },
            React.createElement("div", { className: cn.reportContainerInner, onContextMenu: onContextMenu, onKeyDown: onKeyDown, style: {
                    backgroundColor: backgroundColorTokens(dt).bg,
                    padding: `var(${CSSVars.Padding})`,
                }, "data-testid": exposeTestId() ? 'reportContainer' : undefined, "data-reportthemename": exposeTestId() ? dt.baseReportThemeName : undefined },
                linkBreadcrumb,
                React.createElement("div", { className: cn.surface, style: {
                        flexDirection: headerFlexDirection,
                    } },
                    React.createElement(PromptHeader, { id: "reportHeader", type: "report", viewMode: !state.emptyViewEditable, tokenOverrides: backgroundColorTokens, pageTabRedesign: pageTabRedesign, hiddenNavigation: hideNavigation === true ||
                            ((hideNavigation === undefined || hideNavigation === 'auto') &&
                                state.sectionTabOrder.length <= 1) ||
                            ((_d = state.sections[state.currentSection]) === null || _d === void 0 ? void 0 : _d.hidden), onHover: (hover) => setPromptHeaderHover(hover) }),
                    React.createElement("div", { style: {
                            display: 'flex',
                            flexGrow: 1,
                            flexBasis: 0,
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }, "data-testid": exposeTestId() ? 'reportSections' : undefined },
                        React.createElement(ReportSections, { ref: ref, onTransitionsComplete: actions.sectionSwitchComplete, hideNavigation: linkBreadcrumb || maximized ? true : hideNavigation, promptHeaderHover: promptHeaderHover, currentSection: state.currentSection, sections: state.sections || [], sectionIds: state.sectionTabOrder, onSectionChanged: (_, id) => actions.setNextSection(id), fadeTransition: state.fadeTransition, tabBarTokens: backgroundColorTokens, tabTokens: tabTokens, tabsEditable: state.tabsEditable, pageTabRedesign: pageTabRedesign, onAddNewTab: () => {
                                actions.addNewSection();
                            }, onTabLabelChange: (itemIndex, item, value, type) => {
                                actions.editTabLabel({ itemIndex, item, value, type });
                            }, onTabMenuOpen: (event) => {
                                actions.notifyTabMenuOpen(event);
                            }, onTabMenuItemSelect: (event) => {
                                actions.selectTabMenuItem(event);
                            }, onReorderTabs: actions.notifyReorderTabs, tabDropTypes: state.tabDropTypes, onTabDrop: ({ dropIndex, droppedItem, monitor }) => actions.notifyDrop({
                                itemIndex: dropIndex,
                                item: droppedItem,
                                monitor,
                            }) }))),
                React.createElement(ReportContextMenu, null),
                React.createElement(HiddenSectionDialog, { onContextMenu: onContextMenu }),
                React.createElement(BirdActionMenu, null),
                React.createElement(ExplanationInfoDialog, null),
                React.createElement(ManageColumnsDialog, null))));
    }
});
export const ReportContainer = connect((state) => {
    return {
        objectName: state.bird.name,
        sections: state.sections.byId,
        sectionTabOrder: state.sections.sectionTabOrder,
        currentSection: state.sections.currentSection,
        headerFlexDirection: selectPromptHeaderFlexDirection(state, ReportPromptHeaderId),
        tabsEditable: state.sections.tabsEditable,
        linkLabels: state.sections.linkLabels,
        reportLabel: state.bird.reportLabel,
        transitioning: state.sections.transitioning,
        hiddenSection: state.sections.hiddenSection,
        maximizedVisual: state.bird.maximizedVisual,
        fadeTransition: state.sections.fadeTransition,
        pageTabRedesign: state.sections.pageTabRedesign,
        emptyViewEditable: state.sections.emptyViewEditable,
        tabDropTypes: state.sections.tabDropTypes,
    };
}, (dispatch) => ({
    actions: bindActionCreators(SectionState.actions, dispatch),
    contextMenuActions: bindActionCreators(ReportContextMenuState.actions, dispatch),
}), undefined, { forwardRef: true })(ReportContainerBase);
